import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { TripSummaryWithPermission } from '@poi-planner/shared';

interface TripCardProps {
  trip: TripSummaryWithPermission;
  onDelete: (id: string) => void;
  onShare?: (tripId: string) => void;
}

function formatDateRange(startDate: string, endDate: string): string {
  if (!startDate) return 'No dates set';

  const start = new Date(startDate + 'T00:00:00');
  const end = endDate ? new Date(endDate + 'T00:00:00') : start;

  const startMonth = start.toLocaleDateString('en-US', { month: 'short' });
  const endMonth = end.toLocaleDateString('en-US', { month: 'short' });
  const startDay = start.getDate();
  const endDay = end.getDate();
  const year = start.getFullYear();

  if (startMonth === endMonth) {
    return `${startMonth} ${startDay}–${endDay}, ${year}`;
  }
  return `${startMonth} ${startDay} – ${endMonth} ${endDay}, ${year}`;
}

export function TripCard({ trip, onDelete, onShare }: TripCardProps) {
  const navigate = useNavigate();
  const [showConfirm, setShowConfirm] = useState(false);

  const handleClick = () => {
    navigate(`/trips/${trip.id}`);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (showConfirm) {
      onDelete(trip.id);
    } else {
      setShowConfirm(true);
      setTimeout(() => setShowConfirm(false), 3000);
    }
  };

  const handleShare = (e: React.MouseEvent) => {
    e.stopPropagation();
    onShare?.(trip.id);
  };

  return (
    <div
      onClick={handleClick}
      className="group relative bg-white border border-border rounded-2xl p-5 cursor-pointer transition-all duration-200 hover:shadow-lg hover:shadow-slate-200/50 hover:border-slate-300 hover:-translate-y-0.5"
    >
      {/* Permission Badge */}
      {!trip.isOwner && (
        <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold"
          style={{
            background: trip.permission === 'editor' ? '#fff7ed' : '#eff6ff',
            color: trip.permission === 'editor' ? '#f97316' : '#3b82f6'
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>
            {trip.permission === 'editor' ? 'edit' : 'visibility'}
          </span>
          <span>{trip.permission === 'editor' ? 'Editor' : 'Viewer'}</span>
        </div>
      )}

      {/* Action buttons */}
      <div className="absolute top-3 right-3 flex items-center gap-1">
        {trip.isOwner && onShare && (
          <button
            onClick={handleShare}
            className="flex items-center justify-center size-8 rounded-lg border bg-white border-border text-text-muted opacity-0 group-hover:opacity-100 transition-all duration-200 hover:border-orange-300 hover:text-orange-600 hover:bg-orange-50"
            title="Share trip"
          >
            <span className="material-symbols-outlined text-lg">share</span>
          </button>
        )}
        {trip.isOwner && (
          <button
            onClick={handleDelete}
            className={`flex items-center justify-center size-8 rounded-lg border transition-all duration-200 ${
              showConfirm
                ? 'bg-red-500 border-red-500 text-white opacity-100'
                : 'bg-white border-border text-text-muted opacity-0 group-hover:opacity-100 hover:border-red-200 hover:text-red-500 hover:bg-red-50'
            }`}
            title={showConfirm ? 'Click again to confirm' : 'Delete trip'}
          >
            <span className="material-symbols-outlined text-lg">
              {showConfirm ? 'check' : 'delete'}
            </span>
          </button>
        )}
      </div>

      {/* Trip icon */}
      <div className="flex items-center justify-center size-12 rounded-xl bg-gradient-to-br from-slate-100 to-slate-50 border border-slate-200/50 mb-4">
        <span className="material-symbols-outlined text-2xl text-slate-500">
          explore
        </span>
      </div>

      {/* Trip name */}
      <h3 className="text-base font-bold text-text-primary mb-1 pr-8 line-clamp-2">
        {trip.name}
      </h3>

      {/* Date range */}
      <p className="text-sm text-text-secondary mb-3">
        {formatDateRange(trip.startDate, trip.endDate)}
      </p>

      {/* Footer with day count */}
      <div className="flex items-center gap-2 pt-3 border-t border-border">
        <span className="material-symbols-outlined text-base text-text-muted">
          calendar_today
        </span>
        <span className="text-xs font-semibold text-text-muted">
          {trip.dayCount} {trip.dayCount === 1 ? 'day' : 'days'}
        </span>
      </div>
    </div>
  );
}
